import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Box, Button, Collapse, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  ContentCopyRounded as CopyIcon,
  ExpandLessRounded as ExpandLessIcon,
  ExpandMoreRounded as ExpandMoreIcon,
} from '@mui/icons-material';

interface CodeBlockProps {
  /** What the block shows. */
  text: string;
  /** What the copy button copies, when it is not the text shown (e.g. the unabridged response). */
  copyText?: string;
  /** Wider letter spacing, for machine-readable lines (MRZ). */
  spaced?: boolean;
}

/**
 * Preformatted text — a response, a request example, a vector — in the monospace face, on the
 * canvas tone inside a hairline frame, with a copy button. It wraps instead of scrolling, so the
 * page stays the only scroller.
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({ text, copyText, spaced = false }) => {
  const theme = useTheme();
  const t = useTranslate();
  const notify = useNotify();
  const label = t('shell.code.copy');

  return (
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          pr: 7,
          fontFamily: theme.custom.text.mono,
          fontSize: '0.8125rem',
          lineHeight: 1.6,
          letterSpacing: spaced ? '0.1em' : undefined,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.custom.radius.small,
        }}
      >
        {text}
      </Box>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          onClick={() => {
            navigator.clipboard.writeText(copyText ?? text);
            notify('shell.code.copied', { type: 'info' });
          }}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/** A folded block behind a quiet button: the full JSON of a response, a long vector. */
export const Disclosure: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Button
        variant="text"
        startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {label}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};
